import * as React from 'react';
import { getItemLocation, getItemPosition, getItemSize } from '../resources/Items';
import { useAppDispatch, useAppSelector } from '../hooks';
import { useRef } from 'react';
import { useEffect } from 'react';
import { IItem } from '../slices/inventorySlice';
import PackageBack from '../assets/PackageBack.png';
import { dragItem } from '../slices/uiSlice';
import { gridDropPosition } from './gridDropPosition';

interface Props {
  numberOfTiles?: number;
  tilesPerRow?: number;
  location: number;
  onDrop?: (uid: string, position: number, valid: boolean) => void;
  bg?: string;
  children?: React.ReactNode;
}

export const Grid: React.FC<Props> = ({ numberOfTiles = 0, tilesPerRow = 0, location, onDrop, bg = PackageBack, children }) => {
  const tileWidth = 26,
    tileHeight = 26,
    rowPadding = 1,
    colPadding = 1;

  const grid = [];
  for (let i = 0; i < numberOfTiles; i++) {
    const x = (i % tilesPerRow) * (tileWidth + colPadding);
    const y = Math.floor(i / tilesPerRow) * (tileHeight + rowPadding);

    grid[i] = <GridItem key={i} width={tileWidth} height={tileHeight} x={x} y={y} bg={bg} />;
  }

  return (
    <div style={{ position: 'absolute', width: '100%', height: '100%' }}>
      {grid}
      {children}
      {onDrop && <DropZone numberOfTiles={numberOfTiles} tilesPerRow={tilesPerRow} onDrop={onDrop} location={location} />}
    </div>
  );
};

interface DropZoneProps {
  numberOfTiles: number;
  tilesPerRow: number;
  location: number;
  onDrop: (uid: string, position: number, valid: boolean) => void;
}

const DropZone: React.FC<DropZoneProps> = ({ numberOfTiles, tilesPerRow, location, onDrop }) => {
  const items = useAppSelector((state) => state.inventory.items);
  const draggedItem = useAppSelector((state) => state.ui.dragging.item);

  if (draggedItem === null) {
    return null;
  }

  return (
    <Zone
      numberOfTiles={numberOfTiles}
      tilesPerRow={tilesPerRow}
      items={Object.keys(items)
        .filter((uid) => getItemLocation(items[uid]) === location)
        .map((uid) => items[uid])}
      onDrop={onDrop}
    />
  );
};

interface ZoneProps {
  items: IItem[];
  onDrop: (uid: string, position: number, valid: boolean) => void;
  numberOfTiles: number;
  tilesPerRow: number;
}

function Zone({ items, onDrop, numberOfTiles, tilesPerRow }: ZoneProps) {
  const dispatch = useAppDispatch();
  const mouseReadyRef = useRef<boolean>(false);
  const ref = useRef<HTMLDivElement>(null);
  const [positions, setPositions] = React.useState<number[]>([]);
  const [cellPosition, setCellPosition] = React.useState({ x: -1, y: -1 });
  const draggedItem = useAppSelector((state) => state.ui.dragging.item);

  useEffect(() => {
    mouseReadyRef.current = false;
  }, [draggedItem]);

  useEffect(() => {
    if (draggedItem === null) {
      return;
    }

    const newPositions: number[] = [];

    items.forEach((item) => {
      const { uid } = item;
      const position = getItemPosition(item);

      if (uid === draggedItem.uid) {
        return;
      }

      const x = position % tilesPerRow;
      const y = Math.floor(position / tilesPerRow);

      for (let dy = 0; dy < getItemSize(item).height; dy++) {
        for (let dx = 0; dx < getItemSize(item).width; dx++) {
          newPositions.push(x + dx + (y + dy) * tilesPerRow);
        }
      }
    });

    setPositions(newPositions);
  }, [items, draggedItem]);

  const size = React.useMemo(() => (draggedItem === null ? { width: 0, height: 0 } : getItemSize(draggedItem)), [draggedItem]);
  const valid = React.useMemo(() => {
    if (cellPosition.x < 0 || cellPosition.y < 0) {
      return false;
    }

    const dropPositions: number[] = [];

    for (let dy = 0; dy < size.height; dy++) {
      for (let dx = 0; dx < size.width; dx++) {
        dropPositions.push(cellPosition.x + dx + (cellPosition.y + dy) * tilesPerRow);
      }
    }

    return !positions.some((position) => dropPositions.includes(position));
  }, [cellPosition, positions, size]);

  const onMouseOver = React.useCallback(
    (e: React.MouseEvent) => {
      if (draggedItem === null || ref.current === null) {
        return;
      }

      setTimeout(() => (mouseReadyRef.current = true), 30);

      const bounds = ref.current.getBoundingClientRect();
      const cell=gridDropPosition(e.clientX,e.clientY,bounds,ref.current.offsetWidth||bounds.width,ref.current.offsetHeight||bounds.height,tilesPerRow,numberOfTiles,size);
      if(cell){setCellPosition({x:cell.x,y:cell.y});}else{setCellPosition({x:-1,y:-1});}

    },
    [draggedItem, tilesPerRow, numberOfTiles, size]
  );

  function onClick(e: React.MouseEvent) {
    if (draggedItem === null) {
      return;
    }

    if (mouseReadyRef.current === false) {
      dispatch(dragItem(null));
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    onDrop(draggedItem?.uid, cellPosition.x + cellPosition.y * tilesPerRow, valid);
  }

  function onMouseUp(e: React.MouseEvent) {
    if((e.nativeEvent as MouseEvent & {itemTouchDrag?:boolean}).itemTouchDrag&&draggedItem&&ref.current){
      dropTouch(e);
      return;
    }
    if (mouseReadyRef.current) {
      onClick(e);
    }
  }

  function dropTouch(e:React.MouseEvent|React.PointerEvent) {
    if(draggedItem&&ref.current){
      e.preventDefault();if(e.type==='mouseup')e.stopPropagation();
      const bounds=ref.current.getBoundingClientRect();
      const cell=gridDropPosition(e.clientX,e.clientY,bounds,ref.current.offsetWidth||bounds.width,ref.current.offsetHeight||bounds.height,tilesPerRow,numberOfTiles,size);
      const occupied=new Set<number>();
      items.filter(item=>item.uid!==draggedItem.uid).forEach(item=>{const p=getItemPosition(item),s=getItemSize(item);for(let y=0;y<s.height;y++)for(let x=0;x<s.width;x++)occupied.add(p%tilesPerRow+x+(Math.floor(p/tilesPerRow)+y)*tilesPerRow);});
      onDrop(draggedItem.uid,cell?.position??-1,!!cell&&!cell.cells.some(p=>occupied.has(p)));
      return;
    }
  }

  return (
    <div
      ref={ref}
      style={{ position: 'absolute', width: '100%', height: '100%', touchAction:'none' }}
      onPointerMove={e=>{if(e.pointerType!=='mouse')onMouseOver(e);}}
      onPointerUp={e=>{
        if(e.pointerType==='touch'||e.pointerType==='pen'){
          Object.defineProperty(e.nativeEvent,'itemTouchDropHandled',{value:true,configurable:true});
          dropTouch(e);
        }
      }}
      onMouseMove={onMouseOver}
      onMouseUp={onMouseUp}
      onClick={onClick}>
      {valid && (
        <div
          style={{
            position: 'absolute',
            width: 27 * size.width - 2,
            height: 27 * size.height - 2,
            top: cellPosition.y * 27,
            left: cellPosition.x * 27,
            background: 'green',
            filter: 'opacity(50%)',
            pointerEvents: 'none',
          }}
        />
      )}
    </div>
  );
}

const GridItem: React.FC<{ width: number; height: number; x: number; y: number; bg: string }> = ({ width, height, x, y, bg }) => {
  let style: React.CSSProperties = {
    position: 'absolute',
    top: y,
    left: x,
  };

  return <img src={bg} width={width} height={height} style={style} />;
};
